import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchProducts } from '../../utils/strapiSearch';

// Import RTK Query APIs for listening to cache invalidations
import { enhancedListingApi } from '../apis/endpoints/listingApi';
import { orderApi } from '../apis/endpoints/orderApi';

const initialState = {
  searchTerm: '',
  sortBy: 'trending',
  filters: {
    productType: '',
    forRent: false,
    forSell: false,
    openToShipping: undefined,
    localPickup: {
      enabled: false
    },
    location: null,
    categories: '',
    sizes: '',
    colors: '',
    occasions: '',
    styles: '',
    genders: '',
    brand: '',
    designerSchool: '',
    material: ''
  },
  loading: false,
  dataLoading: false,
  loadingMore: false, // true only while an offset > 0 (pagination) request is in flight
  pendingOffset: null, // offset of the in-flight request, so the same page can't be fetched twice
  error: null,
  loadMoreError: null, // pagination failure — retried manually, never automatically
  products: [],
  totalResults: 0,
  offset: 0,
  hasMore: false,
  isInitialized: false,
  lastSearch: null,
  pageType: null, // 'browse' | 'listings' | null
  layoutChanging: false, // Flag to prevent loading during layout changes
  needsRefresh: false // Flag for force refresh from cache invalidations
};

// Parse URL parameters
const parseUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get('q') || '';
  const sortBy = params.get('sort') || 'trending';
  const filters = {};

  params.forEach((value, key) => {
    if (key !== 'q' && key !== 'sort') {
      try {
        filters[key] = JSON.parse(value);
      } catch {
        filters[key] = value;
      }
    }
  });

  return { searchTerm, sortBy, filters };
};

// Search thunk
export const searchWithAlgolia = createAsyncThunk(
  'filter/searchWithAlgolia',
  async ({ searchTerm, filters, sortBy, offset = 0 }, { rejectWithValue, getState }) => {
    try {
      const state = getState().filter;
      const location = state.filters.location && state.filters.localPickup?.enabled
        ? state.filters.location
        : null;

      const result = await searchProducts({
        searchTerm,
        filters,
        sortBy,
        offset,
        location
      }, getState);

      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Search with term thunk - preserves filters
//
// Only records the term. The search itself is issued by useUrlChangeHandler,
// which watches the filter parameters — firing one here as well would run two
// identical offset-0 searches per keystroke and flash the grid twice.
export const searchWithTerm = createAsyncThunk(
  'filter/searchWithTerm',
  async (searchTerm, { dispatch }) => {
    // Update the search term without resetting other filters
    dispatch(setSearchTerm(searchTerm));
    return null;
  }
);

// URL change listener thunk
export const handleUrlChange = createAsyncThunk(
  'filter/handleUrlChange',
  async (_, { dispatch, getState }) => {
    const { searchTerm, sortBy, filters } = parseUrlParams();
    const state = getState().filter;

    // Only proceed if we're on a valid page type
    if (!state.pageType) {
      return null;
    }

    // Check if any values have changed from the last successful search
    const hasChanged = !state.lastSearch ||
      searchTerm !== state.lastSearch.searchTerm ||
      sortBy !== state.lastSearch.sortBy ||
      JSON.stringify(filters) !== JSON.stringify(state.lastSearch.filters);

    if (!hasChanged && state.isInitialized) {
      // What's on screen already matches the URL, so there's nothing to fetch.
      // The filter reducers optimistically raise `dataLoading` before the search
      // they expect to follow; if no search follows, that flag has to come back
      // down or it blocks pagination for the rest of the session. `pendingOffset`
      // distinguishes "a request is genuinely in flight" from a stale flag.
      if (state.dataLoading && state.pendingOffset === null) {
        dispatch(setDataLoading(false));
      }
      return null;
    }

    // Update parameters without triggering loading state
    dispatch(updateAllParams({
      searchTerm,
      sortBy,
      filters,
      setLoading: false // Don't set loading here, let searchWithAlgolia handle it
    }));

    return dispatch(searchWithAlgolia({ searchTerm, filters, sortBy, offset: 0 }));
  }
);

// Load the next page of results.
//
// Offset is derived from products.length rather than a separate counter so it
// can't drift out of sync with what's actually rendered. `pendingOffset` makes
// the guard airtight: the sentinel can fire again before dataLoading has been
// committed, and without it the same page gets fetched twice and appended twice.
export const loadMoreProducts = createAsyncThunk(
  'filter/loadMoreProducts',
  async (_, { dispatch, getState }) => {
    const state = getState().filter;

    if (
      !state.hasMore ||
      state.loading ||
      state.dataLoading ||
      state.loadingMore ||
      state.layoutChanging ||
      state.loadMoreError
    ) {
      return null;
    }

    const nextOffset = (state.products || []).length;
    if (nextOffset === 0 || nextOffset >= state.totalResults || state.pendingOffset === nextOffset) {
      return null;
    }

    return dispatch(searchWithAlgolia({
      searchTerm: state.searchTerm,
      filters: state.filters,
      sortBy: state.sortBy,
      offset: nextOffset
    }));
  }
);

// Clear filters and trigger search thunk
export const clearAllAndSearch = createAsyncThunk(
  'filter/clearAllAndSearch',
  async (_, { dispatch, getState }) => {
    // First clear the filters
    dispatch(clearAll());

    try {
      // Then trigger a search with empty parameters
      const result = await dispatch(searchWithAlgolia({
        searchTerm: '',
        filters: {},
        sortBy: 'trending',
        offset: 0
      })).unwrap();

      // Make sure loading states are reset
      dispatch(setDataLoading(false));

      return result;
    } catch (error) {
      // Ensure loading states are reset even on error
      dispatch(setDataLoading(false));
      return Promise.reject(error);
    }
  }
);

// Add a new action to force refresh from external cache invalidations
const forceRefreshProducts = createAsyncThunk(
  'filter/forceRefreshProducts',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().filter;

      if (!state.isInitialized || !state.pageType) {
        return; // Don't refresh if not initialized or no page type
      }


      // Use current search parameters to refresh
      const { searchTerm, filters, sortBy } = state;

      // Clear current products and search again
      dispatch(clearAll());
      return dispatch(searchWithAlgolia({
        searchTerm,
        filters,
        sortBy,
        offset: 0
      }));
    } catch (error) {
      console.error('Error in force refresh products:', error);
      return rejectWithValue(error.message);
    }
  }
);

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    updateAllParams: (state, action) => {
      const { searchTerm, sortBy, filters, setLoading = true } = action.payload;
      state.searchTerm = searchTerm;
      state.sortBy = sortBy;
      state.filters = {
        ...state.filters,
        ...filters
      };
      state.offset = 0;
      state.isInitialized = true;
      if (setLoading) {
        state.dataLoading = true;
      }
    },
    setDataLoading: (state, action) => {
      state.dataLoading = action.payload;
      if (!action.payload) {
        state.loadingMore = false;
        state.pendingOffset = null;
      }
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.offset = 0;
      state.dataLoading = true;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
      state.offset = 0;
      state.dataLoading = true;
    },
    setFilter: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
      state.offset = 0;
      state.dataLoading = true;
    },
    setPageType: (state, action) => {
      const newPageType = action.payload;
      const oldPageType = state.pageType;

      // Only proceed if the page type is actually changing
      if (newPageType !== oldPageType) {
        if (newPageType === null) {
          // When unmounting, just clear the page type
          state.pageType = null;
        } else if (newPageType === 'browse' || newPageType === 'listings' || newPageType === 'favorites' || newPageType === 'merchant') {
          // Reset state for new page type
          Object.assign(state, {
            ...initialState,
            pageType: newPageType,
            isInitialized: false // Let handleUrlChange handle the initialization
          });
        }
      }
    },
    clearAll: (state) => {
      const { pageType } = state;
      Object.assign(state, {
        ...initialState,
        pageType,
        loading: false,
        dataLoading: false,
        loadingMore: false,
        pendingOffset: null,
        isInitialized: true,
        hasMore: false,
        error: null
      });
    },
    setLayoutChanging: (state, action) => {
      state.layoutChanging = action.payload;
    },
    clearLoadMoreError: (state) => {
      state.loadMoreError = null;
    },
    optimisticUpdate: (state, action) => {
      state.products = action.payload;
      state.totalResults = action.payload.length;
      state.offset = 0;
      state.hasMore = false;
      state.dataLoading = false;
      state.loadingMore = false;
      state.pendingOffset = null;
    },
    // New action to trigger refresh from cache invalidations
    triggerRefreshFromCache: (state) => {
      // Mark that we need to refresh - will be handled in extraReducers
      state.needsRefresh = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchWithAlgolia.pending, (state, action) => {
        const offset = action.meta.arg?.offset || 0;
        // `loading` blanks the whole grid — CardsContainer renders only a
        // spinner while it's true, which unmounts every loaded card and throws
        // the scroll position back to the top. That's correct for a fresh
        // search, but for pagination the existing list must stay mounted, so
        // only flag dataLoading (the inline spinner beneath the grid).
        if (offset === 0) {
          state.loading = true;
          state.loadingMore = false;
        } else {
          state.loadingMore = true;
        }
        state.pendingOffset = offset;
        state.dataLoading = true;
        state.error = null;
        state.loadMoreError = null;
      })
      .addCase(searchWithAlgolia.fulfilled, (state, action) => {
        const offset = action.meta.arg.offset || 0;
        const { searchTerm, filters, sortBy } = action.meta.arg;
        const incoming = action.payload.products || [];
        let addedCount = incoming.length;

        // A page fetched under filters the user has since changed must not be
        // appended to the new result set. (A fresh search dispatched while
        // page N was still in flight can easily resolve first.)
        const isStalePage = offset > 0 && (
          searchTerm !== state.searchTerm ||
          sortBy !== state.sortBy ||
          JSON.stringify(filters) !== JSON.stringify(state.filters)
        );
        if (isStalePage) {
          // Only retract the in-flight markers if they still belong to this
          // request — a newer search may already own them.
          if (state.pendingOffset === offset) {
            state.loadingMore = false;
            state.pendingOffset = null;
          }
          return;
        }

        if (offset === 0) {
          state.products = incoming;
          state.offset = 0;
        } else {
          // Two pages can overlap when items are added/removed between requests;
          // duplicate ids would break React keys and inflate products.length,
          // which is what the next offset is derived from.
          const seen = new Set(state.products.map(p => p.id));
          const fresh = incoming.filter(p => !seen.has(p.id));
          addedCount = fresh.length;
          state.products = [...state.products, ...fresh];
          state.offset = offset;
        }

        state.totalResults = action.payload.totalResults;
        // A page that adds nothing means there is nothing left to reach,
        // whatever the count endpoint claims. Without this the list length —
        // and so the next offset — would never advance and the sentinel would
        // refetch the same page forever.
        state.hasMore = addedCount > 0 && state.products.length < action.payload.totalResults;
        state.loading = false;
        state.dataLoading = false;
        state.loadingMore = false;
        state.pendingOffset = null;
        state.isInitialized = true;
        state.lastSearch = { searchTerm, filters, sortBy };
      })
      .addCase(searchWithAlgolia.rejected, (state, action) => {
        const failedOffset = action.meta.arg?.offset || 0;

        // A newer request may already own the in-flight markers.
        if (state.pendingOffset === failedOffset) {
          state.loading = false;
          state.dataLoading = false;
          state.loadingMore = false;
          state.pendingOffset = null;
        }

        if (failedOffset > 0) {
          // A failed page fetch must not replace the list that's already on
          // screen with an error, and it must not be retried automatically —
          // the sentinel is still in view, so that would spin forever. Park it
          // behind a manual retry instead.
          state.loadMoreError = action.payload || 'Could not load more products.';
        } else {
          state.error = action.payload;
        }
      })

      // Handle force refresh
      .addCase(forceRefreshProducts.pending, (state) => {
      })
      .addCase(forceRefreshProducts.fulfilled, (state) => {
        state.needsRefresh = false;
      })
      .addCase(forceRefreshProducts.rejected, (state, action) => {
        state.needsRefresh = false;
        console.error('❌ Force refresh products failed:', action.payload);
      })

      // Listen to RTK Query mutation fulfillments that should trigger refresh
      // Listing API mutations
      .addMatcher(
        enhancedListingApi.endpoints.createProduct.matchFulfilled,
        (state, action) => {
          state.needsRefresh = true;
        }
      )
      .addMatcher(
        enhancedListingApi.endpoints.updateProduct.matchFulfilled,
        (state, action) => {
          state.needsRefresh = true;
        }
      )
      .addMatcher(
        enhancedListingApi.endpoints.deleteProduct.matchFulfilled,
        (state, action) => {
          state.needsRefresh = true;
        }
      )

      // Order API mutations (for checkout → transactions refresh)
      .addMatcher(
        orderApi.endpoints.createOrder.matchFulfilled,
        (state, action) => {
          state.needsRefresh = true;
        }
      );
  }
});

export const {
  updateAllParams,
  setDataLoading,
  setSearchTerm,
  setSortBy,
  setFilter,
  setPageType,
  clearAll,
  optimisticUpdate,
  setLayoutChanging,
  clearLoadMoreError,
  triggerRefreshFromCache
} = filterSlice.actions;

// Export the force refresh thunk
export { forceRefreshProducts };

export default filterSlice.reducer; 